import { useEffect, useRef, useState } from "react";
import "./ShareButton.css";

function getShareUrl() {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

// Approx. rendered height of the popover; used to decide flip direction.
const POPOVER_HEIGHT = 320;

function ShareButton({ title, text }) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [toast, setToast] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  // Close popover on outside click and Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Auto-dismiss toast.
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const url = getShareUrl();
  const shareText = text || title || "Découvrez cet événement sur Paris Agenda";

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title, text: shareText, url });
      setOpen(false);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setOpen(true);
      }
    }
  };

  // Open above the button when there isn't enough room below in the viewport.
  const computeDirection = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setDropUp(spaceBelow < POPOVER_HEIGHT && spaceAbove > spaceBelow);
  };

  const handleClick = () => {
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      handleNativeShare();
    } else {
      setOpen((v) => {
        if (!v) computeDirection();
        return !v;
      });
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setToast("Lien copié !");
    } catch {
      setToast("Impossible de copier le lien");
    }
    setOpen(false);
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(shareText);

  const links = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: "bi-whatsapp",
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: "bi-facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      icon: "bi-twitter-x",
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      key: "mail",
      label: "E-mail",
      icon: "bi-envelope",
      href: `mailto:?subject=${encodeURIComponent(title || "Paris Agenda")}&body=${encodedText}%20${encodedUrl}`,
    },
  ];

  return (
    <div className="position-relative d-inline-block" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="pa-btn pa-btn--quiet pa-btn--block"
        onClick={handleClick}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <i className="bi bi-share me-1" aria-hidden="true"></i>
        Partager
      </button>

      {open && (
        <div
          className={`share-popover${dropUp ? " share-popover-up" : ""}`}
          role="menu"
        >
          <ul className="share-popover-list">
            <li>
              <button type="button" onClick={copyLink} role="menuitem">
                <span className="share-icon">
                  <i className="bi bi-link-45deg" aria-hidden="true"></i>
                </span>
                Copier le lien
              </button>
            </li>
            {links.map((l) => (
              <li key={l.key}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  <span className="share-icon">
                    <i className={`bi ${l.icon}`} aria-hidden="true"></i>
                  </span>
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {toast && (
        <div className="share-toast" role="status">
          <i className="bi bi-check-circle me-1" aria-hidden="true"></i>
          {toast}
        </div>
      )}
    </div>
  );
}

export default ShareButton;
